// client/src/helpers/analytics.ts
//
// TRIL-116: thin PostHog wrapper. No-op when VITE_POSTHOG_KEY absent
// (local dev / preview environments). Hashes userId, strips PII-shaped
// keys from props. Vendor-swappable: only this file knows about PostHog.

import posthog from 'posthog-js';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY ?? '';
const POSTHOG_HOST =
  import.meta.env.VITE_POSTHOG_HOST ?? 'https://us.i.posthog.com';
const USERID_HASH_SALT =
  import.meta.env.VITE_POSTHOG_SALT ?? 'trilirivos-default-salt';

const PII_SHAPED_KEYS = new Set([
  'email',
  'username',
  'name',
  'displayName',
  'phoneNumber',
  'bio',
  'location',
  'city',
  'country',
  'zipPostalCode',
  'birthday',
  'dateOfBirth',
  'age',
  'password',
  'url',
  'website',
  'handle',
  'jobTitle',
  'job_title',
  'institution',
  'credentials',
  'fieldOfStudy',
  'field_of_study',
]);

let initialized = false;

function initIfNeeded(): boolean {
  if (!POSTHOG_KEY) return false;
  if (initialized) return true;
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    capture_pageview: false,
    persistence: 'localStorage+cookie',
  });
  initialized = true;
  return true;
}

async function sha256HexShort(input: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(input);
  const hash = await crypto.subtle.digest('SHA-256', data);
  const arr = Array.from(new Uint8Array(hash));
  return arr
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
    .slice(0, 16);
}

function stripPII(props: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(props)) {
    if (PII_SHAPED_KEYS.has(k)) continue;
    if (k === 'userId') continue; // hashed separately
    out[k] = v;
  }
  return out;
}

export function trackEvent(
  name: string,
  props: Record<string, unknown> = {},
): void {
  if (!initIfNeeded()) return;

  void (async () => {
    const userId = typeof props.userId === 'string' ? props.userId : undefined;
    const distinctId = userId
      ? await sha256HexShort(`${USERID_HASH_SALT}:${userId}`)
      : undefined;

    const cleanProps = stripPII(props);
    if (distinctId) cleanProps.distinctId = distinctId;

    posthog.capture(name, cleanProps);
  })();
}

export function shutdownAnalytics(): void {
  if (!initialized) return;
  posthog.reset();
  initialized = false;
}
